import React, { useState, useEffect, useContext } from "react";
import { Input, Tabs, Select } from "antd";
import { useSearchParams, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  getFinancialYearOptions,
} from "../../ReportContainer/utils/financialYearUtils";
import PendingTab from "./PendingTab";
import CompletedTab from "./CompletedTab";
import BilledSalesOrder from "./BilledSalesOrder";
// import AllSalesOrder from "./AllSalesOrder";
import mainContext from "../context/MainContext/mainContext";
import { setSideTab } from "../../../../toolkit/slices/inventories/inventorySlice";
import accessCheckPromise from "../../../../../utils/accessCheck";
import { logoutUser } from "../../../../toolkit/slices/users/userSlice";
import AuthorizationError from "../../AuthorizationError";

const { Search } = Input;
const { Option } = Select;

const SalesOrderContainer = () => {
  const dispatch = useDispatch();
  const {
    id,
    setId,
    searchQuery,
    setSearchQuery,
    bdfNo,
    setBdfNo,
    soNo,
    setSoNo,
    setFetchToggle,
    setPageNumber,
    fetchPartiesAndBrands,
    parties,
    brands,
    fetchInvoiceDataForCreditNote,
    financialYear,
    setFinancialYear,
  } = useContext(mainContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const { id: routeSalesOrderId } = useParams();
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "1");
  const [partySearchText, setPartySearchText] = useState("");
  const [brandSearchText, setBrandSearchText] = useState("");
  const [bdfSearchText, setBdfSearchText] = useState("");
  const [soSearchText, setSoSearchText] = useState("");
  const [activeSearchMode, setActiveSearchMode] = useState(null); // "party" | "brand" | "bdf" | "so" | null
  const [access, setAccess] = useState(false);

  useEffect(() => {
    // Synchronize the activeTab state with the URL parameter
    if (!searchParams.get("tab")) {
      setSearchParams({ tab: "1" });
    } else {
      setActiveTab(searchParams.get("tab"));
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    // Source of truth for selected sales order is now the URL param.
    // Keep context `id` in sync so existing module logic continues to work.
    if (routeSalesOrderId) {
      if (routeSalesOrderId !== id) {
        setId(routeSalesOrderId);
      }
      return;
    }

    if (!routeSalesOrderId && id) {
      setId(null);
    }
  }, [routeSalesOrderId, id]);

  useEffect(() => {
    dispatch(setSideTab(window.location.pathname));
    accessCheckPromise()
      .then((res) => setAccess(res))
      .catch((error) => {
        // Don't force logout on deep-link/module resolution errors.
        // Logout should be reserved for explicit auth failures.
        setAccess(false);
      });
  }, []);

  useEffect(() => {
    if (activeTab === "1") {
      fetchPartiesAndBrands("pending");
    } else if (activeTab === "2") {
      fetchPartiesAndBrands("completed");
    } else if (activeTab === "3") {
      fetchPartiesAndBrands("billed");
      // } else if (activeTab === "4") {
      //   fetchPartiesAndBrands("all");
    }
  }, [activeTab]);

  const tabChange = (key) => {
    // When switching tabs, reset pagination to avoid clamping to another tab's last page.
    setPageNumber(1);
    setActiveTab(key);
    setSearchParams({ tab: key });
  };

  // let tabs = [{ key: "1", label: "Sales Order" }];

  const renderTabContent = () => {
    switch (activeTab) {
      case "1":
        return <PendingTab />;
      case "2":
        return <CompletedTab />;
      case "3":
        return <BilledSalesOrder />;
      // case "4":
      //   return <AllSalesOrder />;
      default:
        return null;
    }
  };

  const applyPartySearch = (value) => {
    const next = value ?? "";
    setPartySearchText(next);
    setSearchQuery(next);
    setBrandSearchText("");
    setBdfSearchText("");
    setSoSearchText("");
    setBdfNo("");
    setSoNo("");
    setActiveSearchMode("party");
    setFetchToggle((prev) => !prev);
  };

  const applyBrandSearch = (value) => {
    const next = value ?? "";
    setBrandSearchText(next);
    setSearchQuery(next);
    setPartySearchText("");
    setBdfSearchText("");
    setSoSearchText("");
    setBdfNo("");
    setSoNo("");
    setActiveSearchMode("brand");
    setFetchToggle((prev) => !prev);
  };

  const handlePartySelectChange = (val) => {
    const next = val ?? "";
    if (!next) {
      setPartySearchText("");
      setSearchQuery("");
      setBdfSearchText("");
      setSoSearchText("");
      setBdfNo("");
      setSoNo("");
      setFetchToggle((prev) => !prev);
      setActiveSearchMode(null);
      return;
    }
    applyPartySearch(next);
  };

  const handleBrandSelectChange = (val) => {
    const next = val ?? "";
    if (!next) {
      setBrandSearchText("");
      setSearchQuery("");
      setBdfSearchText("");
      setSoSearchText("");
      setBdfNo("");
      setSoNo("");
      setFetchToggle((prev) => !prev);
      setActiveSearchMode(null);
      return;
    }
    applyBrandSearch(next);
  };

  const applyBdfSearch = (value) => {
    const next = value ?? "";
    setBdfSearchText(next);
    setBdfNo(next);
    setSearchQuery("");
    setPartySearchText("");
    setBrandSearchText("");
    setSoSearchText("");
    setSoNo("");
    setActiveSearchMode("bdf");
    setFetchToggle((prev) => !prev);
  };

  const applySoSearch = (value) => {
    const next = value ?? "";
    setSoSearchText(next);
    setSoNo(next);
    setSearchQuery("");
    setPartySearchText("");
    setBrandSearchText("");
    setBdfSearchText("");
    setBdfNo("");
    setActiveSearchMode("so");
    setFetchToggle((prev) => !prev);
  };

  const handleBdfChange = (e) => {
    const next = e.target.value;

    if (!next && bdfSearchText && activeSearchMode === "bdf") {
      setBdfSearchText("");
      setBdfNo("");
      setActiveSearchMode(null);
      setFetchToggle((prev) => !prev);
      return;
    }

    setBdfSearchText(next);
  };

  const handleSoChange = (e) => {
    const next = e.target.value;

    if (!next && soSearchText && activeSearchMode === "so") {
      setSoSearchText("");
      setSoNo("");
      setActiveSearchMode(null);
      setFetchToggle((prev) => !prev);
      return;
    }

    setSoSearchText(next);
  };

  if (!access) {
    return <AuthorizationError />;
  }

  return (
    <div
      className="parent-padding-container"
      style={{
        backgroundColor: "white",
        borderRadius: "12px",
        padding: "0px 20px",
      }}
    >
      <div
        style={{
          // backgroundColor: "white",
          padding: "0px 20px",
          borderRadius: "12px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "1rem",
        }}
      >
        <Tabs
          defaultActiveKey="1"
          activeKey={activeTab}
          onChange={tabChange}
          items={
            !id
              ? [
                  { key: "1", label: "Pending" },
                  { key: "2", label: "Completed" },
                  { key: "3", label: "Billed Sales Order" },
                  // { key: "4", label: "All Sales Order" },
                ]
              : searchParams.get("tab") === "1"
              ? [{ key: "1", label: "Pending" }]
              : searchParams.get("tab") === "2"
              ? [{ key: "2", label: "Completed" }]
              : searchParams.get("tab") === "3"
              ? [{ key: "3", label: "Billed Sales Order" }]
              // : searchParams.get("tab") === "4"
              // ? [{ key: "4", label: "All Sales Order" }]
              : []
          }
        />

        {(activeTab === "1" ||
          activeTab === "2" ||
          activeTab === "3"
          // activeTab === "4"
          ) &&
          !id && (
            <div
              style={{
                display: "flex",
                gap: "10px",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <Select
                value={financialYear}
                onChange={(fy) => {
                  setFinancialYear(fy);
                  setPageNumber(1);
                }}
                style={{ width: 120 }}
                placeholder="Financial Year"
              >
                {getFinancialYearOptions().map((fy) => (
                  <Option key={fy} value={fy}>
                    {fy}
                  </Option>
                ))}
              </Select>

              <Select
                placeholder="Search by Party Name"
                allowClear
                showSearch
                optionFilterProp="label"
                filterOption={(input, option) => {
                  const label = String(option?.label ?? "").toLowerCase();
                  const query = String(input ?? "").toLowerCase();
                  if (!query) return true;
                  return label.startsWith(query);
                }}
                value={partySearchText || undefined}
                onChange={handlePartySelectChange}
                style={{ width: 240 }}
                options={(parties || []).map((name) => ({
                  label: name,
                  value: name,
                }))}
              />

              <Select
                placeholder="Search by Brand Name"
                allowClear
                showSearch
                optionFilterProp="label"
                filterOption={(input, option) => {
                  const label = String(option?.label ?? "").toLowerCase();
                  const query = String(input ?? "").toLowerCase();
                  if (!query) return true;
                  return label.startsWith(query);
                }}
                value={brandSearchText || undefined}
                onChange={handleBrandSelectChange}
                style={{ width: 240 }}
                options={(brands || []).map((name) => ({
                  label: name,
                  value: name,
                }))}
              />

              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <Search
                  placeholder="BDF No."
                  value={bdfSearchText}
                  onChange={handleBdfChange}
                  onSearch={() => {
                    if (!bdfSearchText.trim()) return;
                    applyBdfSearch(bdfSearchText);
                  }}
                  style={{ width: 150 }}
                />

                <Search
                  placeholder="SO No."
                  value={soSearchText}
                  onChange={handleSoChange}
                  onSearch={() => {
                    if (!soSearchText.trim()) return;
                    applySoSearch(soSearchText);
                  }}
                  style={{ width: 150 }}
                />

                {(bdfSearchText || soSearchText) && (
                  <button
                    type="button"
                    onClick={() => {
                      setBdfSearchText("");
                      setSoSearchText("");
                      setSearchQuery("");
                      setBdfNo("");
                      setSoNo("");
                      setActiveSearchMode(null);
                      setFetchToggle((prev) => !prev);
                    }}
                    style={{
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      fontSize: "16px",
                      lineHeight: 1,
                      padding: 0,
                      color: "#ff4d4f",
                    }}
                    aria-label="Clear BDF/SO filters"
                    title="Clear"
                  >
                    ❌
                  </button>
                )}
              </div>
            </div>
          )}
      </div>
      {renderTabContent()}
    </div>
  );
};

export default SalesOrderContainer;
